import { useNotify } from "ra-core";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import { fullName, type GulaClient } from "./gulaTypes";
import { decideLead, type LeadDecision } from "./leadDecision";
import { KEEP_DAYS } from "./pipelineHygiene";

const DONE_MESSAGE: Record<LeadDecision, string> = {
  keep: `Sigues con él ${KEEP_DAYS} días más`,
  release: "Lead libre: el primero que lo trabaje se lo queda",
  reactivate: "Pasa a Reactivar",
};

interface LeadDecisionDialogProps {
  client: GulaClient | null;
  onClose: () => void;
  onDone: () => void;
}

/**
 * A lead that is taking too long to close: the owner decides whether to keep
 * it (saying why), give it to another setter or leave it for reactivation.
 */
export const LeadDecisionDialog = ({
  client,
  onClose,
  onDone,
}: LeadDecisionDialogProps) => {
  const notify = useNotify();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => setReason(""), [client?.id]);

  if (!client) return null;

  const run = async (action: LeadDecision) => {
    setBusy(true);
    try {
      await decideLead(client, action, reason);
      notify(DONE_MESSAGE[action], { type: "success" });
      onDone();
    } catch (error: unknown) {
      notify(
        error instanceof Error ? error.message : "No se ha podido guardar",
        { type: "error" },
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>¿Qué hacemos con {fullName(client)}?</DialogTitle>
          <DialogDescription>
            Lleva mucho tiempo sin cerrarse. Si sigues viendo la venta, cuéntalo
            y te lo quedas {KEEP_DAYS} días más. Si no, déjalo libre para otro
            setter o pásalo a Reactivar.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="keep-reason">Por qué sigues con él</Label>
          <Textarea
            id="keep-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Ej.: me ha dicho que lo decide el viernes con su pareja"
            rows={3}
          />
        </div>
        <DialogFooter className="flex-wrap gap-2 sm:justify-between">
          <div className="flex gap-2">
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => run("release")}
            >
              Soltar
            </Button>
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => run("reactivate")}
            >
              A Reactivar
            </Button>
          </div>
          <Button
            disabled={busy || reason.trim().length < 3}
            onClick={() => run("keep")}
          >
            Sigo con él
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
